import { Button, CircularProgress, Typography, useTheme } from "@mui/material";
import { CustomFormField } from "../../../utilis/component/CustomFormElement";
import { useForm } from "react-hook-form";
import { FC, useContext, useEffect, useState } from "react";
import { AllergyContext } from ".";
import { useMutation } from "react-query";
import {
  getAllergyType,
  getseverity,
  getAllergyStatus,
  getAllergen,
  PostAllergy,
} from "../../services";
import { AllergyListType, parseAllergyApiDate, postAllergy } from "./types";
import { useParams } from "react-router-dom";
import { clinicId } from "../../../constants";
import usePopup from "../../../hooks/usePopup";
import { FormOnChangeEventType } from "../../types";
import { HL7ContexTriggerContex } from "../../../context/HL7MessageTrigger/HL7MessageTriggerProvider";
import dayjs, { Dayjs } from "dayjs";
import useAuthentication from "@hooks/useAuthentication";
import { EmrContext } from "../../";

const AddAllergy: FC<{ close?: () => void }> = ({ close }) => {
  const theme = useTheme();
  const { setTrigger } = useContext(HL7ContexTriggerContex);
  const { showSnackbar } = usePopup();
  const {user} = useAuthentication()
  const { encounterId, patientId } = useParams();
  const { control, handleSubmit, reset, watch } = useForm();
  const { setEdit, edit, setSuccess } = useContext(AllergyContext);
  const { triggerConsultationStart } = useContext(EmrContext);

  const [identificationDate, setIdentificationDate] = useState<Dayjs | null>(
    null
  );
  const {
    mutate: fetchAllergyType,
    data: allergyType,
    isLoading: allergyTypeLoading,
  } = useMutation(getAllergyType);
  const {
    mutate: fetchAllergen,
    data: allergen,
    isLoading: allergenLoading,
  } = useMutation(getAllergen);
  const {
    mutate: fetchAllergySeverity,
    data: allergySeverity,
    isLoading: allergySeverityLoading,
  } = useMutation(getseverity);
  const {
    mutate: fetchAllergyStatus,
    data: allergyStatus,
    isLoading: allergyStatusLoading,
  } = useMutation(getAllergyStatus);
  const { mutate: saveallergy, isLoading: saveLoading } = useMutation(
    PostAllergy,
    {
      onSuccess: (result) => {
        if (result.responseIndicator == "success") {
          showSnackbar({
            message: result.responseMessage,
            open: true,
            variant: "success",
            duration: 2000,
          });
          setSuccess(true);
          setTrigger({
            encounterId: encounterId as string,
            tab: "allergy",
          });
          triggerConsultationStart();
          reset({
            allergyType: null,
          });
        } else {
          showSnackbar({
            message: result.responseMessage,
            open: true,
            variant: "warning",
            duration: 2000,
          });
        }
      },
      onError: () => {
        showSnackbar({
          message: "Something went wrong !",
          open: true,
          variant: "success",
          duration: 2000,
        });
      },
    }
  );

const prepareAllergy = (value: AllergyListType): postAllergy => {
  return {
    id: value.id,
    typeId: value?.allergyType?.allergyTypeId,
    allergenId: value?.allergen?.allergenId || 0,
    description: value?.description || "",
    reactions: value?.reactions || "",
    severityId: value?.severity?.allergySeverityId || null,
    identificationDate: value?.identificationDate
      ? dayjs(value.identificationDate, "YYYY-MM-DD", true).format("YYYY-MM-DD")
      : null,

    resolvedDate: value?.resolvedDate
      ? dayjs(value.resolvedDate, "YYYY-MM-DD", true).format("YYYY-MM-DD")
      : null,

    encounterId: parseInt(encounterId as string),
    clinicId,
    status: 1,
    patientId: parseInt(patientId as string),
    createdById: Number(user?.userId),
    createdDate: null,
    hl7MsgStatus: 0,
    actionCode: null,
    allergyStatus: value?.lifeCycleStatus?.id || null,
  };
};

  const onSubmit = (value: any) => {
    let AllergyData;
    if (Boolean(edit)) {
      AllergyData = prepareAllergy({ id: edit?.id, ...value });
      saveallergy(AllergyData);
      setEdit(null);
    } else {
      AllergyData = prepareAllergy({ id: null, ...value });
      saveallergy(AllergyData);
    }
  };
  const fetchInitialData = () => {
    fetchAllergyType();
    fetchAllergySeverity();
    fetchAllergyStatus();
  };

  const handleClose = () => {
    close && close();
    setEdit(null);
  };

  const onChange = (event: FormOnChangeEventType) => {
    fetchAllergen(event?.target?.value?.allergyTypeId);
    reset({
      allergyType: event?.target?.value,
    });
  };

  const handleIdentificationDate = (event: FormOnChangeEventType) => {
    const { target } = event;
    if (target.value) {
      setIdentificationDate(dayjs(target.value));
    }else{
      setIdentificationDate(null);
    }
  };

  const checkAllergyStatus = {
    2:true,
    3:true,
  }

  useEffect(() => {
    fetchInitialData();
  }, []);

  useEffect(() => {
    if (edit) {
      fetchAllergen(edit.allergyType.allergyTypeId);
      const normalizedIdentificationDate = parseAllergyApiDate(
        edit.identificationDate,
      );
      const normalizedResolvedDate = parseAllergyApiDate(
        edit.resolvedDate ?? null,
      );
      setIdentificationDate(
        normalizedIdentificationDate
          ? dayjs(normalizedIdentificationDate, "YYYY-MM-DD", true)
          : null,
      );
      reset({
        ...edit,
        identificationDate: normalizedIdentificationDate,
        resolvedDate: normalizedResolvedDate,
      });
    }
  }, [edit, reset]);
  return (
    <div className="border-2 rounded">
      <div className="border-b  rounded-t p-2" style={{}}>
        <Typography color={theme.palette.primary.main} fontWeight={600}>
          {Boolean(edit) ? "Update" : "Add"} Allergy
        </Typography>
      </div>

<form onSubmit={handleSubmit(onSubmit)} className="grid gap-3 grid-cols-6 p-2">
  {/* Allergy Type */}
  <div className="col-span-2">
    <CustomFormField
      name="allergyType"
      control={control}
      element="autocomplete"
      fieldProps={{ label: "Allergy type" }}
      rules={{ required: "Allergy type required", onChange }}
      options={allergyType?.response || []}
      loading={allergyTypeLoading}
      autocompleteProps={{
        isOptionEqualToValue: (option, value) =>
          option.allergyTypeId == value.allergyTypeId,
        getOptionLabel: (option) => option.allergyTypeName,
      }}
    />
  </div>

  {/* Allergen */}
 
    <div className="col-span-4">
      <CustomFormField
        name="allergen"
        control={control}
        element="autocomplete"
        fieldProps={{ label: "Allergen" }}
        rules={ { required: "Allergen required" }}
        options={allergen?.response || []}
        loading={allergenLoading}
        autocompleteProps={{
          isOptionEqualToValue: (option, value) =>
            option.allergenId == value.allergenId,
          getOptionLabel: (option) => option.allergenName,
        }}
      />
    </div>


  {/* Severity */}

    <div className="col-span-2">
      <CustomFormField
        name="severity"
        control={control}
        element="autocomplete"
        fieldProps={{ label: "Severity" }}
        rules={{ required: "Severity required" }}
        options={allergySeverity?.response || []}
        loading={allergySeverityLoading}
        autocompleteProps={{
          isOptionEqualToValue: (option, value) =>
            option.allergySeverityId == value.allergySeverityId,
          getOptionLabel: (option) => option.allergySeverityName,
        }}
      />
    </div>

  {/* Status */}
    <div className="col-span-2">
      <CustomFormField
        name="lifeCycleStatus"
        control={control}
        element="autocomplete"
        fieldProps={{ label: "Status" }}
        rules={{ required: "Status required" }}
        options={allergyStatus?.response || []}
        loading={allergyStatusLoading}
        autocompleteProps={{
          isOptionEqualToValue: (option, value) => option.id == value.id,
          getOptionLabel: (option) => option.description,
        }}
      />
    </div>

  {/* Identification Date */}
  <div className="col-span-1">
    <CustomFormField
      name="identificationDate"
      control={control}
      element="datepicker"
      fieldProps={{ label: "Identification date" }}
      calenderProps={{ maxDate: dayjs() }}
      rules={{ required: "Identification date required for active cases", onChange: handleIdentificationDate }}
    />
  </div>

    <div className="col-span-1">
      <CustomFormField
        name="resolvedDate"
        control={control}
        element="datepicker"
        fieldProps={{ label: "Inactive Date" }}
        rules={{ required: Boolean(watch("lifeCycleStatus")?.id && checkAllergyStatus[watch("lifeCycleStatus")?.id as keyof typeof checkAllergyStatus]) ? "Inactive Date required": false }}
        calenderProps={
          Boolean(identificationDate)
            ? { minDate: identificationDate as Dayjs }
            : {}
        }
      />
    </div>


  {/* Reaction */}
  
    <div className="col-span-2">
      <CustomFormField
        name="reactions"
        control={control}
        element="input"
        fieldProps={{ label: "Reaction", multiline: true }}
        rules={{ required: "Reaction required" }}
      />
    </div>


  {/* Description (always shown) */}
  <div className="col-span-2">
    <CustomFormField
      name="description"
      control={control}
      element="input"
      fieldProps={{ label: "Description", multiline: true }}
       rules={{ required: "" }}
    />
  </div>

  {/* Buttons */}
  <div className="col-span-2 flex justify-end items-center gap-3">
    <Button
      color="success"
      type="submit"
      disabled={saveLoading}
      endIcon={saveLoading && <CircularProgress size={18} />}
    >
      save
    </Button>
    {close && (
      <Button color="error" onClick={handleClose}>
        cancel
      </Button>
    )}
  </div>
</form>

    </div>
  );
};

export default AddAllergy;
